import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/** Content blocks and cards that fade up as they scroll into view, on every screen. */
const TARGETS = [
  '[class*="-inner"] > *',        // phone screens: the blocks of each scroll body
  '.bb-body > *',                 // branches / reservations bodies
  '[data-cat] > *',               // menu items, section by section
  '.dk-shell > *', '.dk-grid > *', '.dk-card',   // desktop columns, grids and cards
  '.grid > *',                    // card grids (branches)
  'app-desk-item-card',
].join(',');

/** Never animated: pinned chrome, overlays, and the home page (it has its own reveals). */
const SKIP = [
  'app-home', '.bb-sheet-scrim', '[sheetFooter]',
  'app-page-header', '.bb-menu-head', '.bb-orders-head', '.bb-search-head', '.bb-res-head', '.bb-head',
  '.bb-pin-foot', '.bb-res-bar', 'app-desktop-footer',
].join(',');

/** After this long on a screen, re-rendered blocks that are already on screen just appear. */
const SETTLE_MS = 1200;

/**
 * Scroll reveal for every routed screen, applied once on the shell's page container.
 * New content (route changes, async data, list renders) is picked up through a
 * MutationObserver; each block gets `.reveal` and fades up (styles.css) when it first
 * enters the viewport, with blocks revealed together staggered slightly. Once played,
 * the classes are removed so the element's own transitions (hover etc.) are untouched.
 * Off with prefers-reduced-motion or without IntersectionObserver.
 */
@Directive({ selector: '[appAutoReveal]', standalone: true })
export class AutoRevealDirective implements OnInit, OnDestroy {
  private io?: IntersectionObserver;
  private mo?: MutationObserver;
  private sub?: Subscription;
  private pending = new Set<Element>();
  private frame = 0;
  private navAt = performance.now();

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone, private router: Router) {}

  ngOnInit(): void {
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined') return;

    this.sub = this.router.events.pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => (this.navAt = performance.now()));

    this.zone.runOutsideAngular(() => {
      this.io = new IntersectionObserver((entries) => {
        const shown = entries.filter((e) => e.isIntersecting).map((e) => e.target as HTMLElement);
        shown.forEach((el, i) => this.play(el, Math.min(i, 6) * 60));
      }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });

      this.mo = new MutationObserver((records) => {
        for (const r of records) r.addedNodes.forEach((n) => { if (n instanceof Element) this.pending.add(n); });
        this.schedule();
      });
      this.mo.observe(this.host.nativeElement, { childList: true, subtree: true });
      this.pending.add(this.host.nativeElement);
      this.schedule();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.mo?.disconnect();
    this.io?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  /** Batch DOM scans to one per frame. */
  private schedule(): void {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.scan(); });
  }

  private scan(): void {
    const roots = Array.from(this.pending).filter((n) => n.isConnected);
    this.pending.clear();
    const settled = performance.now() - this.navAt > SETTLE_MS;
    const seen = new Set<Element>();
    for (const root of roots) {
      const found = Array.from(root.querySelectorAll(TARGETS));
      if (root.matches(TARGETS)) found.unshift(root);
      for (const el of found) {
        if (seen.has(el) || !(el instanceof HTMLElement)) continue;
        seen.add(el);
        if (el.closest(SKIP)) continue;
        const pos = getComputedStyle(el).position;
        if (pos === 'sticky' || pos === 'fixed') continue;
        // A list re-render after the screen settled (e.g. a cart quantity change)
        // must not make on-screen rows fade in again.
        if (settled && this.onScreen(el)) continue;
        el.classList.add('reveal');
        this.io!.observe(el);
      }
    }
  }

  private onScreen(el: HTMLElement): boolean {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < innerHeight;
  }

  private play(el: HTMLElement, delay: number): void {
    this.io!.unobserve(el);
    if (delay) el.style.transitionDelay = `${delay}ms`;
    el.classList.add('is-visible');
    // Hand the element back to its own styles once the fade has played.
    setTimeout(() => {
      el.classList.remove('reveal', 'is-visible');
      if (delay) el.style.transitionDelay = '';
    }, delay + 800);
  }
}
